import { bannerImagesSection } from '../shared/components/avatar-banner/banner-images-editor.component';
import { DwMetaDataFormPreferences } from '@devwareapps/devware-cap';
import { appearanceEditorSection } from '../shared/components/appearance-editor/appearance-editor.config';
import { DwUserEntity } from '../../meta-data/app-meta-data.service';

export type ProfileStyleIdentity = Pick<DwUserEntity, 'FirstName' | 'LastName' | 'UserPictureMediaId' | 'UserBackgroundMediaId'>;

export const FITNESS_PROFILE_CONFIG_PART = 'fitness-profile';
export const FITNESS_PROFILE_CONFIG_KEY = 'dw_User-' + FITNESS_PROFILE_CONFIG_PART;

// CAP owns loading, validation, section saves, media uploads and unsaved-change handling.
// Keep this configuration separate from the administrative dw_User/UserProfile forms.
const columns = { columns: 2, columnLayoutMode: 'grid12', columnDetails: [] };
const form = (names: string[], readOnly = false): any => ({
    sectionType: 'form', showTitle: false, formStyle: 'verticleForm',
    columnPreferences: columns, isReadOnly: readOnly,
    fields: names.map(attrName => ({ attrName, columnItemPreferences: {} })),
});
const container = (sections: any[]): any => ({ sectionType: 'container', showTitle: false,
    columnPreferences: { ...columns, columns: 1 }, sections });
const related = (parentItem: string, childItem: string, propertyName: string,
    foreignKey: string, section: any, noRowsMessage: string, readOnly = false): any => ({
    sectionType: 'childDataRepeater', sectionKey: 'profile-' + propertyName,
    parentItem, childItem, propertyName,
    relationshipName: `${parentItem}|${childItem}|${foreignKey}`,
    hideAddAndRemove: true, hideAdd: true, hideRemove: true,
    autoAddRows: false, loadWithParent: true, excludeFromSave: readOnly, noRowsMessage, section,
});
export function createProfileEditPreferences(): DwMetaDataFormPreferences {
    return Object.assign(new DwMetaDataFormPreferences(), {
        hidePrimaryKey: true, loadDisplayProperties: true,
        formStyleName: 'standard', contextNames: ['mdFormContext', 'challenge-context'],
        requiredContextNames: ['mdFormContext', 'challenge-context'],
        section: container([
            bannerImagesSection(false),
            form(['FirstName', 'LastName', 'AlternateName', 'MobilePhone']),
            related('dw_User', 'UserProfile', 'UserProfile', 'UserId', container([
                form(['UserDescription']),
                form(['MyChallengeCode'], true),
                { ...form([]), showTitle: true, title: 'Donation preferences',
                    description: 'These settings apply to your past and future donations from all streaks.',
                    fields: [
                        { attrName: 'DonationsAnonymous', label: 'Show my donations anonymously',
                            infoText: 'Your donations appear in the Anonymous total and do not count toward team totals.', columnItemPreferences: {} },
                        { attrName: 'IncludeDonationsInTeamTotals', label: 'Include my donations in team totals',
                            infoText: 'Turn off to leave your named donations out of team totals. Anonymous donations are always excluded from team totals.', columnItemPreferences: {} },
                    ] },
            ]), 'Your Streaker profile will be available after registration.'),
        ]),
    });
}

/** Inline editor excludes the banner so media and identity remain steady above the panel. */
export function createInlineProfileEditPreferences(): DwMetaDataFormPreferences {
    const preferences = createProfileEditPreferences();
    (preferences.section as any).sections = (preferences.section as any).sections.slice(1);
    return preferences;
}

export function createFitnessProfilePreferences(streakAgnostic = false): DwMetaDataFormPreferences {
    const preferences = Object.assign(new DwMetaDataFormPreferences(), { hidePrimaryKey: true, loadDisplayProperties: true,
        useButtonDefaults: false, contextNames: ['mdFormContext', 'challenge-context'], requiredContextNames: ['mdFormContext', 'challenge-context'],
        section: container([]) });
    // Community data belongs to custom components; CAP retains section saves and dirty tracking.
    (preferences.section as any).sections = [{ sectionType:'component', componentName:'fitness-profile-avatar-banner', config:{editable:true} }, {
        sectionType: 'fitnessProfileFlip', sections: [{ sectionType: 'component', componentName: 'fitness-profile-content', config: {streakAgnostic} }],
    }];
    return preferences;
}

export function createProfileStylePreferences(identity?: ProfileStyleIdentity): DwMetaDataFormPreferences {
    const editor = { ...appearanceEditorSection(false),
        previewName: [identity?.FirstName, identity?.LastName].filter(Boolean).join(' '),
        avatarMediaId: identity?.UserPictureMediaId, bannerMediaId: identity?.UserBackgroundMediaId };
    return Object.assign(new DwMetaDataFormPreferences(), {
        hidePrimaryKey: true, section: container([editor]),
    });
}
